import React, { Fragment } from 'react';
import styled, { css, useTheme } from 'styled-components';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import AutoToNextSubsectionListener from 'Containers/Classroom/AutoToNextSubsectionListener';
import { Box, SvgIcon, Flex, ButtonOutlined as BaseButtonOutlined } from 'Components/Base';

import { Text, Button, VideoOverlayWrapper } from './VideoOverlayContent';
import { getAutoNextVideoVariant } from './Util';
import { VIDEO_SIZE, VIDEO_VARIANTS } from './constant';

export const ButtonOutlined = styled(({ text, icon, buttonProps, ...props }) => (
  <BaseButtonOutlined borderRadius="100px" {...buttonProps.sysButtonProps} {...props}>
    <Text
      color="inherit"
      fontWeight="medium"
      fontFamily="content"
      lineHeight="title"
      {...buttonProps.messageProps}
    >
      {text}
    </Text>
  </BaseButtonOutlined>
))`
  ${({ theme: { colors = {} }, buttonProps }) => css`
    && {
      border: ${buttonProps.sysButtonProps.borderWidth} solid ${colors.primary};
      color: ${colors.primary};
    }
  `}
`;

const TruncatedTitle = styled(Text)`
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  display: -webkit-box;
  text-overflow: ellipsis;
  overflow: hidden;
`;

const AutoNextSubsectionContent = ({
  active,
  isShowOverlay,
  isLastSubsectionInCourse,
  currentSubsectionTitle,
  nextSubsectionTitle,
  variant,
  videoSize,
  onNextSubsectionClick,
  onResumeClick,
  onReplayClick,
  onCancelClick,
  ...props
}) => {
  const { t } = useTranslation('classroom');
  const theme = useTheme();
  const { width: videoWidth } = videoSize ?? {};
  let controlButtonProps = {
    text: t('VIDEO_OVERLAY.PAUSED.BUTTON.RESUME'),
    icon: SvgIcon.ICON_LIST.PLAY,
    onClick: onResumeClick,
  };
  if (variant === VIDEO_VARIANTS.ENDED) {
    controlButtonProps = {
      text: t('VIDEO_OVERLAY.ENDED.BUTTON.CURRENT_SUBSECTION'),
      onClick: onCancelClick,
    };
  }
  if (isLastSubsectionInCourse) {
    controlButtonProps = {
      text: t('VIDEO_OVERLAY.ENDED.BUTTON.REPLAY'),
      icon: SvgIcon.ICON_LIST.REPLAY,
      onClick: onReplayClick,
    };
  }

  const { headerProps, titleProps, buttonProps } = getAutoNextVideoVariant(videoWidth, theme);

  return (
    <VideoOverlayWrapper active={active} videoSize={videoWidth} {...props}>
      {(isLastSubsectionInCourse ||
        (variant === VIDEO_VARIANTS.PAUSED && currentSubsectionTitle)) && (
        <Box alignSelf="center" display={'block'}>
          <Text color="videoOverlayHeader" textAlign={'center'} {...headerProps}>
            {t('VIDEO_OVERLAY.PAUSED.LEARNING')}
          </Text>
          <TruncatedTitle textAlign={'center'} {...titleProps}>
            {currentSubsectionTitle}
          </TruncatedTitle>
        </Box>
      )}
      {variant === VIDEO_VARIANTS.ENDED && videoWidth >= VIDEO_SIZE.LARGE && nextSubsectionTitle && (
        <Fragment>
          <Text color="videoOverlayHeader" {...headerProps}>
            {t('VIDEO_OVERLAY.ENDED.AUTO_NEXT_SUBSECTION')}
          </Text>
          <TruncatedTitle textAlign={'center'} {...titleProps}>
            {nextSubsectionTitle}
          </TruncatedTitle>
        </Fragment>
      )}
      {isShowOverlay && !isLastSubsectionInCourse && <AutoToNextSubsectionListener />}
      <Flex
        flexDirection={videoWidth >= VIDEO_SIZE.LARGE ? 'row' : 'column'}
        justifyContent="space-around"
        alignItems="center"
        {...buttonProps.wrapperProps}
        mt={videoWidth >= VIDEO_SIZE.LARGE ? buttonProps.wrapperProps.mt : 0}
      >
        {isLastSubsectionInCourse ? (
          <Button my={1} mx={4} buttonProps={buttonProps} {...controlButtonProps} />
        ) : (
          videoWidth >= VIDEO_SIZE.SMALL && (
            <ButtonOutlined my={1} mx={2} buttonProps={buttonProps} {...controlButtonProps} />
          )
        )}

        {nextSubsectionTitle && (
          <Fragment>
            {videoWidth >= VIDEO_SIZE.SMALL ? (
              <Button
                my={1}
                mx={2}
                text={t('VIDEO_OVERLAY.ENDED.BUTTON.AUTO_NEXT_SUBSECTION')}
                icon={SvgIcon.ICON_LIST.PLAY_CIRCLE_OUTLINE}
                onClick={onNextSubsectionClick}
                buttonProps={buttonProps}
                style={{
                  borderColor: theme.colors.primary,
                  borderWidth: buttonProps.sysButtonProps.borderWidth,
                }}
              />
            ) : (
              <SvgIcon
                style={{ cursor: 'pointer' }}
                icon={SvgIcon.ICON_LIST.PLAY_CIRCLE_OUTLINE}
                size="24px"
                onClick={onNextSubsectionClick}
              />
            )}
          </Fragment>
        )}
      </Flex>
    </VideoOverlayWrapper>
  );
};

AutoNextSubsectionContent.propTypes = {
  active: PropTypes.bool,
  isShowOverlay: PropTypes.bool,
  isLastSubsectionInCourse: PropTypes.bool,
  currentSubsectionTitle: PropTypes.string,
  nextSubsectionTitle: PropTypes.string,
  variant: PropTypes.oneOf(Object.values(VIDEO_VARIANTS)).isRequired,
  onReplayClick: PropTypes.func,
  onNextSubsectionClick: PropTypes.func,
  onResumeClick: PropTypes.func,
  onCancelClick: PropTypes.func,
};

AutoNextSubsectionContent.defaultProps = {
  active: false,
  onReplayClick: () => {},
  onNextSubsectionClick: () => {},
  onResumeClick: () => {},
  onCancelClick: () => {},
};

export default AutoNextSubsectionContent;
